import type { Stay } from '@/services/reserva'

/** Pasos del check-in con los ids del bundle (CheckIn.dc.html `step=`). */
export type StepId = '1' | '1b' | '1c' | '2' | '2c' | '3' | '3b' | '4' | '5' | '6' | '7' | '8'
export type Persona = 'holder' | 'companion'
/** register: registro completo de una persona · arrival: solo la hora de llegada · complete: completar datos o firma */
export type FlowMode = 'register' | 'arrival' | 'complete'
export type CaptureMethod = 'scan' | 'qr' | 'manual'
/** Cómo eligió rellenar los datos al pulsar Registrar (app actual: CollectDataForm, inventario D33) */
export type EntryChoice = 'capture' | 'manual'

export const CHECKIN_BASE = '/checkin'
export const stepPath = (id: StepId) => (id === '1' ? CHECKIN_BASE : `${CHECKIN_BASE}/paso/${id}`)
export const completePath = (travelerId: string) => `${CHECKIN_BASE}/completar/${travelerId}`

type RulesStay = Pick<Stay, 'registration'>

/**
 * Pasos que ve una persona (inventario D30): la cara solo si la propiedad la pide y el documento se ha
 * escaneado con MobbScan (hoy tampoco se pide tras MiDNI ni a mano); la hora de llegada solo el titular.
 * Si al pulsar Registrar eligió rellenar a mano (D33), no hay paso de captura: del 2 se pasa al formulario.
 * Si eligió la captura, primero confirma país y tipo de documento (antes de iniciar MobbScan); nombre y
 * apellidos salen de la lectura y el email (y el móvil del titular) se piden en la revisión.
 */
export function registrationSteps(stay: RulesStay, persona: Persona, method: CaptureMethod | null, entry: EntryChoice | null = null): StepId[] {
  const manualEntry = entry === 'manual'
  const withFace = !manualEntry && stay.registration.rules.forceSelfie && method !== 'qr' && method !== 'manual'
  const captureEntry = entry === 'capture'
  return [...(captureEntry ? (['2c'] as const) : (['2'] as const)), ...(manualEntry ? [] : (['3'] as const)), '3b', ...(withFace ? (['4'] as const) : []), '5', ...(persona === 'holder' ? (['6'] as const) : []), '7']
}

/** "Paso X de N": 1 y 8 cuentan; 1b y 1c comparten número con 1, y 3b con 3 si hay captura (bundle). */
export function progressFor(id: StepId, stay: RulesStay, persona: Persona, method: CaptureMethod | null, entry: EntryChoice | null = null): { step: number; total: number } {
  const steps = registrationSteps(stay, persona, method, entry)
  const withCapture = steps.includes('3')
  const numbered = ['1', ...steps.filter((s) => s !== '3b' || !withCapture), '8']
  const base = id === '1b' || id === '1c' ? '1' : id === '3b' && withCapture ? '3' : id
  const index = numbered.indexOf(base)
  return { step: index >= 0 ? index + 1 : 1, total: numbered.length }
}

/** Paso anterior y siguiente dentro del registro. */
export function neighbours(id: StepId, stay: RulesStay, persona: Persona, method: CaptureMethod | null, entry: EntryChoice | null = null): { prev: StepId; next: StepId } {
  const list: StepId[] = ['1', ...registrationSteps(stay, persona, method, entry), '8']
  const i = list.indexOf(id)
  if (id === '1b' || id === '1c') return { prev: '1', next: list[1] ?? '2' }
  return { prev: list[Math.max(0, i - 1)] ?? '1', next: list[Math.min(list.length - 1, i + 1)] ?? '8' }
}

/** Atrás lleva el nombre del paso anterior real (bundle B2; sin cara: "Documento", sin llegada: "Registro legal"). */
export function backLabelKey(prev: StepId): string {
  const byPrev: Partial<Record<StepId, string>> = {
    '1': 'v2.checkin.atras.ocupantes',
    '2': 'v2.checkin.atras.datos',
    '2c': 'v2.checkin.atras.documento',
    '3': 'v2.checkin.atras.documento',
    '3b': 'v2.checkin.atras.documento',
    '4': 'v2.checkin.atras.foto',
    '5': 'v2.checkin.atras.registroLegal',
    '6': 'v2.checkin.atras.llegada',
  }
  return byPrev[prev] ?? 'v2.checkin.atras.estancia'
}
